import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Search, BadgeCheck, Heart } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { resolveAssetUrl } from "@/lib/assetUrl";
import SaveToBoardDialog from "@/components/SaveToBoardDialog";
import TrendingBoardsRail from "@/components/TrendingBoardsRail";
import PersonalisedRail from "@/components/PersonalisedRail";
import LatestDropsRail from "@/components/LatestDropsRail";

/**
 * Blinterest — visual discovery inside The Blk Community.
 *
 * Design notes:
 *  - Deep-navy background, gold accent, masonry column layout (no library —
 *    plain flexbox column-count so we don't pull in another dep).
 *  - Category chips are a horizontal scroll rail on mobile, wrap on desktop.
 *  - Infinite scroll uses IntersectionObserver on a sentinel div.
 *  - Guest users can still browse — the Save button prompts them to sign in.
 */
export default function BlinterestPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [categories, setCategories] = useState([{ slug: "for-you", label: "For You" }]);
  const [category, setCategory] = useState(searchParams.get("cat") || "for-you");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [pendingSearch, setPendingSearch] = useState(searchParams.get("q") || "");
  const [pins, setPins] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [saveDialogPin, setSaveDialogPin] = useState(null);

  const sentinelRef = useRef(null);

  // Fetch category list once
  useEffect(() => {
    axios
      .get(`${API}/blinterest/categories`)
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => {});
  }, []);

  const fetchFeed = useCallback(
    async ({ append } = { append: false }) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category === "following") {
          params.set("following", "true");
        } else {
          params.set("category", category);
        }
        params.set("limit", "24");
        if (search) params.set("search", search);
        if (append && cursor) params.set("cursor", cursor);
        const r = await axios.get(`${API}/blinterest/feed?${params.toString()}`, {
          withCredentials: true,
        });
        const next = r.data.pins || [];
        setPins((prev) => (append ? [...prev, ...next] : next));
        setCursor(r.data.next_cursor);
        setHasMore(Boolean(r.data.next_cursor));
      } catch (e) {
        // Silent — empty feed is a valid state
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [category, search, cursor],
  );

  // Re-fetch when category or search changes; reset cursor.
  useEffect(() => {
    setCursor(null);
    setHasMore(true);
    fetchFeed({ append: false });
  }, [category, search]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchFeed({ append: true });
      },
      { rootMargin: "600px" },
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, fetchFeed]);

  const changeCategory = (slug) => {
    setCategory(slug);
    const params = new URLSearchParams(searchParams);
    params.set("cat", slug);
    setSearchParams(params);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(pendingSearch);
    const params = new URLSearchParams(searchParams);
    if (pendingSearch) params.set("q", pendingSearch);
    else params.delete("q");
    setSearchParams(params);
  };

  const handleSaveClick = (pin, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!user) {
      // Same UX as favorites — never block browsing.
      navigate("/join?returnTo=" + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    setSaveDialogPin(pin);
  };

  return (
    <div className="min-h-screen bg-tbc-navy" data-testid="blinterest-page">
      <Navbar />

      {/* ============== HERO ============== */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 text-center">
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-gold tracking-tight"
            data-testid="blinterest-title"
          >
            BLINTEREST
          </h1>
          <p className="mt-2 text-white/70 font-body text-sm sm:text-base">
            by The BLK Community
          </p>
          <p className="mt-1 text-tbc-gold/80 font-body text-xs sm:text-sm italic">
            Find what you love. Support who made it.
          </p>

          <form
            onSubmit={onSearchSubmit}
            className="mt-6 max-w-2xl mx-auto flex gap-2"
            data-testid="blinterest-search-form"
          >
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Search Blinterest"
                className="pl-10 bg-white text-tbc-navy border-0 h-11 rounded-full"
                data-testid="blinterest-search-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy h-11 px-6 rounded-full"
              data-testid="blinterest-search-submit"
            >
              Search
            </Button>
          </form>
        </div>
      </section>

      {/* ============== LATEST DROPS RAIL ============== */}
      {/* Community-wide hero rail highlighting the freshest published
          drops. Renders null if the backend has nothing to show, so the
          rest of the page shifts up gracefully on a brand-new deployment. */}
      <LatestDropsRail placement="blinterest" limit={12} />

      {/* ============== CATEGORY CHIPS ============== */}
      <section className="border-b border-white/5 bg-tbc-navy sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {/* "Following" is only useful for signed-in users */}
            {user && (() => {
              const active = category === "following";
              return (
                <button
                  type="button"
                  onClick={() => changeCategory("following")}
                  className={`px-4 py-1.5 rounded-full text-sm font-body whitespace-nowrap border transition-colors ${
                    active
                      ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
                      : "bg-transparent text-white/80 border-white/25 hover:border-tbc-gold/60"
                  }`}
                  data-testid="blinterest-cat-following"
                >
                  Following
                </button>
              );
            })()}
            {categories.map((c) => {
              const active = c.slug === category;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => changeCategory(c.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-body whitespace-nowrap border transition-colors ${
                    active
                      ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
                      : "bg-transparent text-white/80 border-white/25 hover:border-tbc-gold/60"
                  }`}
                  data-testid={`blinterest-cat-${c.slug}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== FEED + RAIL ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 min-w-0">
            {category === "for-you" && !search && <PersonalisedRail />}
            {loading ? (
              <div className="columns-2 md:columns-3 lg:columns-3 gap-4">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={`blinterest-skeleton-${i}`}
                    className="mb-4 rounded-2xl bg-white/5 animate-pulse break-inside-avoid"
                    style={{ height: 200 + ((i * 47) % 180) }}
                  />
                ))}
              </div>
            ) : pins.length === 0 ? (
              <div className="text-center py-16 text-white/70">
                <p className="font-heading text-2xl text-white mb-2">
                  {category === "following" ? "No Finds from your follows yet" : "No Finds here yet"}
                </p>
                <p className="font-body text-sm">
                  {category === "following"
                    ? "Follow a few businesses to fill Discover with their latest Drops."
                    : search
                    ? "Try a different search term or category."
                    : "Check back soon — new Drops land here every day."}
                </p>
              </div>
            ) : (
              <>
                <div className="columns-2 md:columns-3 lg:columns-3 gap-4" data-testid="blinterest-feed">
                  {pins.map((pin) => (
                    <PinCard
                      key={pin.pin_id}
                      pin={pin}
                      onSave={(e) => handleSaveClick(pin, e)}
                    />
                  ))}
                </div>
                <div ref={sentinelRef} className="h-8" />
                {loadingMore && (
                  <p className="text-center text-white/60 text-sm py-4 font-body">Loading more…</p>
                )}
                {!hasMore && pins.length > 0 && (
                  <p className="text-center text-white/40 text-xs py-6 font-body">
                    You&apos;ve seen every Find in this Discover.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Right-hand rail — desktop only */}
          <div className="hidden lg:block">
            <div className="sticky top-40">
              <TrendingBoardsRail />
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SaveToBoardDialog
        pin={saveDialogPin}
        open={Boolean(saveDialogPin)}
        onOpenChange={(o) => !o && setSaveDialogPin(null)}
      />
    </div>
  );
}

// ---- Pin card -----------------------------------------------------------
function PinCard({ pin, onSave }) {
  return (
    <Link
      to={`/blinterest/pin/${pin.pin_id}`}
      className="mb-4 block break-inside-avoid rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors group"
      data-testid={`pin-card-${pin.pin_id}`}
    >
      <div className="relative">
        <img
          src={resolveAssetUrl(pin.image_url)}
          alt={pin.title || "Find"}
          className="w-full h-auto object-cover"
          loading="lazy"
        />
        <button
          type="button"
          onClick={onSave}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`pin-save-${pin.pin_id}`}
          aria-label="Keep this Find"
        >
          <Heart className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3">
        <p className="text-white font-body text-sm line-clamp-2">{pin.title}</p>
        {pin.business && (
          <p className="text-white/60 font-body text-xs mt-1 flex items-center gap-1">
            <span className="truncate">{pin.business.name}</span>
            {pin.business.is_verified && (
              <BadgeCheck className="w-3 h-3 text-tbc-gold flex-none" />
            )}
          </p>
        )}
      </div>
    </Link>
  );
}
